import { useTranslation } from "react-i18next";
import DefinitionListItem from "../../../ui/layout/DefinitionListItem";
import { labelOf, useVersionLabels } from "./useVersionLabels";
import VersionLabelText from "./VersionLabelText";
import { processTerm } from "../../../api/content/processTerms";
import { classText } from "../../workflows/inputCards/typeWords";

interface BoundVersionsProps {
  projectId: string;
  /** The Workflow version, `<item id>@<blob>`, and its name where known. */
  workflow: { readonly reference: string; readonly name: string | null } | null;
  /** `<collection id>@<commit>`. */
  contentVersion: string;
  corpusVersion: string | null;
  /** A turn's step and prompt versions, `<item id>@<blob>`. */
  step?: string | null;
  prompt?: string | null;
}

interface BoundRow {
  readonly key: "workflow" | "step" | "prompt" | "contentVersion" | "corpusVersion";
  readonly reference: string;
  /** The name before the version; `null`: the project content. */
  readonly name: string | null;
  /** What shows without a label. */
  readonly unlabelled: string | null;
}

/**
 * The Workflow, content and corpus versions a Thread or turn is bound to, by their labels
 * (ADR-0033 D1, F29): "Extract claims 1.2.0 · Better date handling". No exact references:
 * the Thread's Details show no hashes (F67); the log and its export keep them.
 */
export default function BoundVersions({ projectId, workflow, contentVersion, corpusVersion, step = null, prompt = null }: BoundVersionsProps) {
  const { t } = useTranslation();
  const items: BoundRow[] = [
    ...(workflow === null ? [] : [{ key: "workflow" as const, reference: workflow.reference, name: workflow.name ?? t("threads.versions.workflow"), unlabelled: workflow.name }]),
    ...(step === null ? [] : [{ key: "step" as const, reference: step, name: t("threads.versions.step"), unlabelled: null }]),
    ...(prompt === null ? [] : [{ key: "prompt" as const, reference: prompt, name: t("threads.versions.prompt"), unlabelled: null }]),
  ];
  const labels = useVersionLabels(projectId, {
    itemVersions: items.map((row) => row.reference),
    collectionVersions: corpusVersion === null ? [contentVersion] : [contentVersion, corpusVersion],
  });
  const rows: BoundRow[] = [
    ...items,
    { key: "contentVersion", reference: contentVersion, name: null, unlabelled: null },
    ...(corpusVersion === null ? [] : [{ key: "corpusVersion" as const, reference: corpusVersion, name: t("versions.corpus"), unlabelled: null }]),
  ];
  return (
    <dl className="space-y-1">
      {rows.map((row) => (
        <DefinitionListItem key={row.key} term={row.key === "contentVersion" ? classText(processTerm("ContentVersion"), t) : t(`threads.versions.${row.key}`)}>
          <VersionLabelText name={row.name} label={labelOf(labels, row.reference)} unlabelled={row.unlabelled} />
        </DefinitionListItem>
      ))}
    </dl>
  );
}
